import type { InvitePayload } from '@/lib/startParam';

/**
 * IL REGISTRO DEI TIPI DI ATTERRAGGIO — dove un payload di `start_param`
 * diventa una rotta.
 *
 * ═══ PERCHE' VIVE QUI E NON IN `lib/logto.ts` ═══
 * (decisione or52 «deeplink-orc», 06/09) Il modulo di autenticazione e'
 * attraversato da OGNI login di OGNI utente. Se possedesse anche il registro
 * degli atterraggi, aggiungere un tipo nuovo — una scheda torneo, una news —
 * significherebbe mettere le mani nel punto piu' delicato della catena per una
 * ragione che con l'autenticazione non c'entra niente. Qui invece un tipo nuovo
 * e' una riga in questo file e nient'altro.
 *
 * ═══ LA REGOLA DI FORMA, RATIFICATA ═══
 * Il payload e' `<tipo>_<resto>`; si divide sul PRIMO `_` soltanto, e il resto
 * e' OPACO al dispatcher — chi smista non interpreta il carico.
 * ⚠️ E' anche la ragione per cui il prefisso e' `inv_bg_` e non `bg_`: il primo
 * segmento e' l'unica cosa che il dispatcher legge, e mettendoci il GIOCO il
 * registro dei tipi diventerebbe un registro dei giochi — dove una news o una
 * scheda torneo non potrebbero mai entrare.
 *
 * ⛔ NON coniare tipi in anticipo. Un tipo si conia quando ha un consumatore
 * vero: un prefisso inventato «per dopo» e' un vincolo che nessuno rinegozia,
 * perche' quando servira' sembrera' gia' deciso.
 */

/**
 * La rotta su cui portare l'utente per questo payload, o `null` se non c'e'
 * niente da fare.
 *
 * Pura di proposito: non naviga, non tocca lo storage, non guarda l'ora. Chi la
 * chiama decide QUANDO; questo file decide solo DOVE.
 */
export function routeForStartParam(payload: InvitePayload, ctx: ContestoAtterraggio = {}): string | null {
  switch (payload.kind) {
    case 'invite':
      // `/invito/<codice>` e non `/backgammon/match/<id>`: non esiste un match a
      // cui puntare — l'invito e' un bearer e il match lo crea l'accettazione.
      return `/invito/${payload.code}`;
    case 'sfida':
      // Il tab «Sfide» di Amici con la sfida in evidenza (B3b-1). L'id e' un input NON FIDATO: si codifica, e chi lo
      // riceve lo confronta con la lista che l'utente ha gia' (`hooks/useEvidenzaSfida.ts`), mai con una GET per id.
      return `/friends?tab=duels&sfida=${encodeURIComponent(payload.challengeId)}`;
    case 'incontro':
      // La rotta e' SINCRONA e PURA, e dove stia l'incontro APERTO di questo utente non si sa da un payload: lo dicono i suoi
      // tornei e i suoi incontri, che sono dati di rete. Qui si porta alla schermata che li legge e poi decide
      // (`routes/IncontroLanding.tsx`: all'incontro se ce n'e' uno aperto, altrimenti alla scheda del torneo). Il tid e' un input
      // NON FIDATO: si codifica, e li' decide SOLO se chiedere gli incontri (e' fra i miei?), mai quale incontro aprire.
      return `${INCONTRO_ROUTE_PREFIX}${encodeURIComponent(payload.tournamentId)}`;
    case 'guarda':
      // «Guarda» della DM «sta giocando» (r38): la visione in sola lettura della partita. La rotta `backgammon/live/:id` esiste SOLO
      // con i due flag (`routes/router.tsx`) e sta dietro `<RoleGate role="beta">`: senza una delle due cose un salto porterebbe a
      // una rotta che non c'e' o a un pannello «serve il ruolo». Il default e' NIENTE SALTO (la Hub): chi non dichiara la build e il
      // ruolo non salta. L'id e' gia' un UUID (regex chiusa del parser), ma e' un input NON FIDATO: si codifica comunque.
      return ctx.rottaBackgammonLive === true && ctx.beta === true
        ? `${GUARDA_ROUTE_PREFIX}${encodeURIComponent(payload.matchId)}`
        : null;
    case 'provaSpider':
      // La prova D3 dello Spider (si toglie nella PR del motore dello Spider, con la rotta). Salta SOLO con la rotta della build E il ruolo `beta`: la rotta la da` un helper
      // della zona del solitario (`lib/solitario/rottaSpiderProva.ts`) col confronto letterale del flag, cosi' questo file resta puro e senza `env`. Altrimenti niente salto (la Hub).
      return ctx.beta === true && ctx.rottaSpiderProva
        ? payload.stato === 'pesante'
          ? `${ctx.rottaSpiderProva}?stato=pesante`
          : ctx.rottaSpiderProva
        : null;
    default: {
      // ESAUSTIVO: un `kind` nuovo senza il suo `case` non compila. A runtime (un payload che non e' della forma dichiarata)
      // non si naviga da nessuna parte.
      const nonGestito: never = payload;
      void nonGestito;
      return null;
    }
  }
}

/**
 * Cio' che serve sapere di QUESTA build e di QUESTO utente per decidere un atterraggio che dipende da loro. Passato da chi chiama
 * (questo file resta puro e senza `env`: lo esercitano anche i cancelli con il solo Node). I default sono i VERSI SICURI: non dichiarare
 * niente = nessun salto.
 */
export interface ContestoAtterraggio {
  /** `backgammon/live/:id` esiste in questa build (`featureBackgammonEnabled` E `featureBackgammonLiveEnabled`). */
  rottaBackgammonLive?: boolean;
  /** L'utente ha il ruolo `beta`. */
  beta?: boolean;
  /** La rotta della prova D3 dello Spider, SE esiste in questa build (la da` `lib/solitario/rottaSpiderProva.ts`; `undefined` in produzione). Si toglie nella PR del motore dello Spider. */
  rottaSpiderProva?: string;
}

/** Il prefisso della visione: la rotta `backgammon/live/:id`. */
export const GUARDA_ROUTE_PREFIX = '/backgammon/live/';

/** Il prefisso di rotta che `usePendingInvite` usa per non rimbalzare su se stesso. */
export const INVITE_ROUTE_PREFIX = '/invito/';

/** Idem per `inc_<tid>`: la landing `/inc/<tid>` (`routes/IncontroLanding.tsx`). Il path del router e' `inc/:tournamentId`. */
export const INCONTRO_ROUTE_PREFIX = '/inc/';
